import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { convertToUTC } from '../../util/convert_timezone';
import dateService from '../../services/dateService';
import TasksAPI from '../../services/tasksService';
import TaskOutcomeModal from './TaskOutcomeModal';
import { isEngagementType, requiresOutcome, getTaskTypeMeta } from '../../constants/taskTypeMeta';
import { taskErrorMessage, allowedOutcomesFrom, isOutcomeRequiredError } from '../../util/taskErrors';
import { toast } from 'react-toastify';
import { formatPhoneDisplay, telHref, renderWithPhoneLinks } from '../../util/phoneUtils';

const SNOOZE_OPTIONS = [
  { label: 'In 1 hour', hours: 1 },
  { label: 'In 4 hours', hours: 4 },
  { label: 'Tomorrow morning', tomorrow: true },
  { label: 'Next week', days: 7 }
];

// Badge fill and the text on it, as a pair (the fills flip to tints in dark).
const PRIORITY_COLORS = {
  urgent: { bg: 'var(--color-danger-solid)', text: 'var(--color-on-danger-solid)' },
  high: { bg: 'var(--color-orange-solid)', text: 'var(--color-on-orange-solid)' },
  normal: { bg: 'var(--color-info-solid)', text: 'var(--color-on-info-solid)' },
  low: { bg: 'var(--color-bg-inverse)', text: 'var(--color-text-inverse)' }
};

const TARGET_ROLE_LABELS = {
  owner: 'Owner',
  manager: 'Manager',
  technician: 'Technician',
  front_desk: 'Front Desk'
};

class TaskActionModal extends Component {
  state = {
    showSnoozeMenu: false,
    acting: false,
    showOutcome: false,
    outcomeError: null,
    // Authoritative option list from a server 400, when it disagrees with ours.
    allowedOutcomes: null
  };

  componentDidUpdate(prevProps) {
    if (prevProps.task?.task_id !== this.props.task?.task_id) {
      this.setState({ showSnoozeMenu: false, acting: false, showOutcome: false, outcomeError: null, allowedOutcomes: null });
    }
  }

  handleComplete = async () => {
    const { task, user, onActionComplete } = this.props;
    // Some task types require an outcome — open the picker instead of completing
    // directly. NOT isEngagementType: invoice_recovery is gated but not green.
    if (requiresOutcome(task.task_type)) {
      this.setState({ showOutcome: true, outcomeError: null, allowedOutcomes: null });
      return;
    }
    this.setState({ acting: true });
    try {
      await TasksAPI.complete(task.task_id, user.User_id);
      window.dispatchEvent(new Event('refreshNavbarCounts'));
      onActionComplete();
    } catch (err) {
      console.error('TaskActionModal: Error completing task', err);
      this.setState({ acting: false });
      // A server-side gate this client does not know about yet: open the picker
      // with the authoritative list rather than failing the user twice.
      if (isOutcomeRequiredError(err)) {
        this.setState({
          showOutcome: true,
          outcomeError: taskErrorMessage(err),
          allowedOutcomes: allowedOutcomesFrom(err),
        });
        return;
      }
      toast.error(taskErrorMessage(err, 'Could not complete the task. Please try again.'));
    }
  };

  handleOutcomeConfirm = async ({ resolution_type, completion_note }) => {
    const { task, user, onActionComplete } = this.props;
    this.setState({ acting: true, outcomeError: null });
    try {
      await TasksAPI.complete(task.task_id, user.User_id, { resolution_type, completion_note });
      this.setState({ showOutcome: false, acting: false });
      window.dispatchEvent(new Event('refreshNavbarCounts'));
      onActionComplete();
    } catch (err) {
      console.error('TaskActionModal: Error completing engagement task', err);
      if (err.response?.status === 404) {
        // Task was auto-resolved elsewhere — treat as done.
        this.setState({ showOutcome: false, acting: false });
        window.dispatchEvent(new Event('refreshNavbarCounts'));
        onActionComplete();
        return;
      }
      this.setState({
        acting: false,
        outcomeError: taskErrorMessage(err, 'Could not complete the task. Please choose an outcome and try again.'),
        allowedOutcomes: allowedOutcomesFrom(err) || this.state.allowedOutcomes,
      });
    }
  };

  handleSnooze = async (option) => {
    const { task, onActionComplete } = this.props;
    let localTime;
    if (option.tomorrow) {
      localTime = moment().add(1, 'day').startOf('day').hour(8);
    } else if (option.days) {
      localTime = moment().add(option.days, 'days').startOf('day').hour(8);
    } else {
      localTime = moment().add(option.hours, 'hours');
    }
    const snoozeUntil = convertToUTC(localTime).format('YYYY-MM-DD HH:mm:ss');
    this.setState({ acting: true });
    try {
      await TasksAPI.snooze(task.task_id, snoozeUntil);
      window.dispatchEvent(new Event('refreshNavbarCounts'));
      onActionComplete();
    } catch (err) {
      console.error('TaskActionModal: Error snoozing task', err);
      this.setState({ acting: false });
      toast.error(taskErrorMessage(err, 'Could not snooze the task. Please try again.'));
    }
  };

  handleViewDetails = () => {
    const { task, onHide } = this.props;
    onHide();
    if (task.entity_type === 'request' && task.entity_id) {
      this.props.history.push('/requestimates', { selectRequestId: task.entity_id, fromToday: true });
    } else {
      this.props.history.push('/tasks', { selectTaskId: task.task_id, fromToday: true });
    }
  };

  render() {
    const { show, onHide, task } = this.props;
    const { showSnoozeMenu, acting, showOutcome, outcomeError } = this.state;

    if (!task) return null;

    const localDueDate = task.due_date ? dateService.fromAPIDate(task.due_date) : null;
    const isOverdue = localDueDate && localDueDate.isBefore(moment(), 'day');
    const priColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal;
    const hasEntity = task.entity_type && task.entity_id;
    const typeMeta = isEngagementType(task.task_type) ? getTaskTypeMeta(task.task_type) : null;
    const localReminderAt = task.task_type === 'consultation' && task.reminder_at
      ? moment(task.reminder_at, 'YYYY-MM-DD HH:mm')
      : null;

    return (
      <Modal show={show} onHide={onHide} centered>
        {/* Header: title + priority badge, red if overdue */}
        <Modal.Header closeButton>
          <Modal.Title style={{
            fontSize: 16, fontWeight: 600,
            color: isOverdue ? 'var(--color-danger)' : undefined
          }}>
            {typeMeta && (
              <i className={typeMeta.icon} title={typeMeta.label} style={{ color: typeMeta.accent, marginRight: 6 }} />
            )}
            {task.title}
            {task.task_type === 'inbound_call' && task.call_count > 1 && (
              <span
                className="badge ml-1"
                title={`Contacted ${task.call_count} times`}
                style={{ backgroundColor: 'var(--color-danger-solid)', color: 'var(--color-on-danger-solid)', fontSize: '0.65rem', verticalAlign: 'middle' }}
              >
                &times;{task.call_count}
              </span>
            )}
            {' '}
            <span className="badge" style={{ backgroundColor: priColor.bg, color: priColor.text, fontSize: '0.65rem', verticalAlign: 'middle' }}>
              {(task.priority || 'normal').toUpperCase()}
            </span>
            {task.target_role && (
              <>
                {' '}
                <span className="badge" style={{ backgroundColor: 'var(--color-bg-inset)', color: 'var(--color-text-primary)', fontSize: '0.65rem', verticalAlign: 'middle' }}>
                  <i className="fa-sharp fa-regular fa-user mr-1" style={{ fontSize: '0.55rem' }} />
                  {TARGET_ROLE_LABELS[task.target_role] || task.target_role}
                </span>
              </>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Inbound caller — E.164 tap-to-call; entity may be null (unresolved caller) */}
          {task.task_type === 'inbound_call' && (
            <div className="mb-3">
              {!task.entity_type && (
                <div style={{ color: 'var(--color-text-secondary)' }}>Unknown caller</div>
              )}
              {task.caller_phone_e164 && (
                <div>
                  <a href={telHref(task.caller_phone_e164)} style={{ color: 'var(--color-info)', fontWeight: 600 }}>
                    <i className="fa-sharp fa-regular fa-phone mr-1" />
                    {formatPhoneDisplay(task.caller_phone_e164)}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Entity contact info — name, phone, email */}
          {(task.entity_name || task.entity_phone || task.entity_email) && (
            <div className="mb-3">
              {task.entity_name && (
                <div><strong>{task.entity_name}</strong></div>
              )}
              {task.entity_phone && (
                <div>
                  <a href={`tel:${task.entity_phone.replace(/[^\d+]/g, '')}`} style={{ color: 'var(--color-info)' }}>
                    <i className="fa-sharp fa-regular fa-phone mr-1" />{task.entity_phone}
                  </a>
                </div>
              )}
              {task.entity_email && (
                <div>
                  <a href={`mailto:${task.entity_email}`} style={{ color: 'var(--color-info)' }}>
                    <i className="fa-sharp fa-regular fa-envelope mr-1" />{task.entity_email}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Next Action — primary content */}
          {task.next_action && (
            <div className="mb-3" style={{
              backgroundColor: 'var(--color-bg-panel)',
              borderRadius: 8,
              padding: '12px 16px',
              fontSize: '0.95rem',
              lineHeight: 1.5
            }}>
              {renderWithPhoneLinks(task.next_action, 'var(--color-info)')}
            </div>
          )}

          {/* Due Date — consultations show the scheduled visit date AND time */}
          {(localReminderAt && localReminderAt.isValid()) ? (
            <div className="mb-3">
              <span style={{
                color: isOverdue ? 'var(--color-danger)' : 'var(--color-text-primary)',
                fontWeight: isOverdue ? 600 : 400,
                fontSize: '0.9rem'
              }}>
                <i className="fa-sharp fa-regular fa-calendar-clock mr-1" />
                {isOverdue ? 'Overdue: ' : 'Scheduled: '}
                {localReminderAt.format('MMM D, YYYY [at] h:mm A')}
              </span>
            </div>
          ) : localDueDate ? (
            <div className="mb-3">
              <span style={{
                color: isOverdue ? 'var(--color-danger)' : 'var(--color-text-primary)',
                fontWeight: isOverdue ? 600 : 400,
                fontSize: '0.9rem'
              }}>
                <i className="fa-sharp fa-regular fa-calendar mr-1" />
                {isOverdue ? 'Overdue: ' : 'Due: '}
                {localDueDate.format('MMM D, YYYY')}
              </span>
            </div>
          ) : null}

          {/* Action buttons */}
          <div className="jv-btn-group-spaced mb-3">
            <button
              type="button"
              className="jv-btn jv-btn-primary"
              onClick={this.handleComplete}
              disabled={acting}
            >
              Complete
            </button>
            <div className="task-snooze-dropdown">
              <button
                type="button"
                className="jv-btn jv-btn-secondary"
                onClick={() => this.setState({ showSnoozeMenu: !showSnoozeMenu })}
                disabled={acting}
              >
                Snooze
              </button>
              {showSnoozeMenu && (
                <div className="task-snooze-menu">
                  {SNOOZE_OPTIONS.map((opt, i) => (
                    <button
                      key={i}
                      className="task-snooze-option"
                      onClick={() => this.handleSnooze(opt)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {hasEntity && (
              <button
                type="button"
                className="jv-btn jv-btn-tertiary"
                onClick={this.handleViewDetails}
              >
                View
              </button>
            )}
          </div>

          {/* Context / Description — muted, below buttons */}
          {task.description && (
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
              <small className="text-muted" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Context
              </small>
              <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.85rem', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                {task.description}
              </p>
            </div>
          )}
        </Modal.Body>

        {/* Outcome picker — required for engagement task completion */}
        <TaskOutcomeModal
          show={showOutcome}
          task={task}
          onHide={() => this.setState({ showOutcome: false, outcomeError: null })}
          onConfirm={this.handleOutcomeConfirm}
          submitting={acting}
          error={outcomeError}
          allowedOutcomes={this.state.allowedOutcomes}
        />
      </Modal>
    );
  }
}

export default withRouter(TaskActionModal);
